import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import yaml from 'js-yaml';
import Footer from './Footer';
import PlatformSelector from './PlatformSelector';
import ImageModal from './ImageModal';
import ImageUploader from './ImageUploader';
import QuestionStep from './QuestionStep';
import TestStep from './TestStep';
import TestCase from './TestCase';
import StepConditionalLogic from './StepConditionalLogic';
import YamlEditor from './YamlEditor';
import { formatEmojiText, getImageUrl } from '../utils/helpers';

function PlanBuilder() {
  const { id } = useParams(); // Get plan ID from URL if editing
  const [plan, setPlan] = useState({
    title: '',
    description: '',
    platforms: [],
    test_cases: []
  });
  const [selectedTestCaseIndex, setSelectedTestCaseIndex] = useState(0);
  const [yamlText, setYamlText] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [createdPlanId, setCreatedPlanId] = useState('');
  const [stepFilters, setStepFilters] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [collapsedSteps, setCollapsedSteps] = useState(new Set());
  const [collapsedTestCases, setCollapsedTestCases] = useState(new Set());

  useEffect(() => {
    updateYamlFromPlan();
  }, [plan]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      setCreatedPlanId(id);
      loadExistingPlan(id);
    }
  }, [id]);

  const loadExistingPlan = async (planId) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/plan/${planId}`);
      setPlan(response.data.plan);
      
      // Start with all steps collapsed when loading a plan
      const allStepIndices = new Set();
      response.data.plan.test_cases.forEach((testCase, tcIndex) => {
        testCase.steps.forEach((step, stepIndex) => {
          allStepIndices.add(`${tcIndex}-${stepIndex}`);
        });
      });
      setCollapsedSteps(allStepIndices);
      
      setMessage('Plan loaded successfully for editing!');
      setMessageType('success');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to load plan for editing');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const updateYamlFromPlan = () => {
    try {
      const yamlString = yaml.dump(plan, { indent: 2 });
      setYamlText(yamlString);
    } catch (error) {
      console.error('Error converting to YAML:', error);
    }
  };

  const updatePlanFromYaml = () => {
    try {
      const parsedPlan = yaml.load(yamlText);
      setPlan(parsedPlan);
      setMessage('YAML updated successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Invalid YAML format');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const addTestCase = () => {
    const newTestCase = {
      id: `case-${Date.now()}`,
      title: '',
      description: '',
      steps: []
    };
    setPlan(prev => ({
      ...prev,
      test_cases: [...prev.test_cases, newTestCase]
    }));
    setSelectedTestCaseIndex(plan.test_cases.length);
  };

  const updateTestCase = (index, field, value) => {
    setPlan(prev => ({
      ...prev,
      test_cases: prev.test_cases.map((testCase, i) => 
        i === index ? { ...testCase, [field]: value } : testCase
      )
    }));
  };

  const removeTestCase = (index) => {
    setPlan(prev => ({
      ...prev,
      test_cases: prev.test_cases.filter((_, i) => i !== index)
    }));
    if (selectedTestCaseIndex >= plan.test_cases.length - 1) {
      setSelectedTestCaseIndex(Math.max(0, plan.test_cases.length - 2));
    }
  };

  const addStep = (stepType = 'test') => {
    const newStep = {
      id: `step-${Date.now()}`,
      title: '',
      description: '',
      ui_images: [],
      step_type: stepType
    };
    
    if (stepType === 'question') {
      newStep.question = {
        type: 'single',
        options: ['', ''],
        images: [],
        option_images: {}
      };
    }
    
    if (plan.test_cases.length === 0) {
      addTestCase();
    }
    setPlan(prev => ({
      ...prev,
      test_cases: prev.test_cases.map((testCase, i) => 
        i === selectedTestCaseIndex ? {
          ...testCase,
          steps: [...testCase.steps, newStep]
        } : testCase
      )
    }));
  };

  const updateStep = (index, field, value) => {
    setPlan(prev => ({
      ...prev,
      test_cases: prev.test_cases.map((testCase, i) => 
        i === selectedTestCaseIndex ? {
          ...testCase,
          steps: testCase.steps.map((step, stepI) => 
            stepI === index ? { ...step, [field]: value } : step
          )
        } : testCase
      )
    }));
  };

  const updateQuestionOption = (stepIndex, optionIndex, value) => {
    setPlan(prev => ({
      ...prev,
      test_cases: prev.test_cases.map((testCase, i) => 
        i === selectedTestCaseIndex ? {
          ...testCase,
          steps: testCase.steps.map((step, stepI) => 
            stepI === stepIndex ? {
              ...step,
              question: {
                ...step.question,
                options: step.question.options.map((option, optI) =>
                  optI === optionIndex ? value : option
                )
              }
            } : step
          )
        } : testCase
      )
    }));
  };

  const addQuestionOption = (stepIndex) => {
    setPlan(prev => ({
      ...prev,
      test_cases: prev.test_cases.map((testCase, i) => 
        i === selectedTestCaseIndex ? {
          ...testCase,
          steps: testCase.steps.map((step, stepI) => 
            stepI === stepIndex ? {
              ...step,
              question: {
                ...step.question,
                options: [...step.question.options, '']
              }
            } : step
          )
        } : testCase
      )
    }));
  };

  const removeQuestionOption = (stepIndex, optionIndex) => {
    setPlan(prev => ({
      ...prev,
      test_cases: prev.test_cases.map((testCase, i) => 
        i === selectedTestCaseIndex ? {
          ...testCase,
          steps: testCase.steps.map((step, stepI) => 
            stepI === stepIndex ? {
              ...step,
              question: {
                ...step.question,
                options: step.question.options.filter((_, optI) => optI !== optionIndex)
              }
            } : step
          )
        } : testCase
      )
    }));
  };

  const updateStepFilters = (stepIndex, value) => {
    setStepFilters(prev => ({
      ...prev,
      [stepIndex]: value
    }));
  };

  const handleQuestionImageUpload = async (stepIndex, files) => {
    let planId = createdPlanId || id;
    
    if (!planId) {
      try {
        // Create a minimal plan to get an ID
        const minimalPlan = {
          title: plan.title || 'Untitled Plan',
          description: plan.description || '',
          platforms: plan.platforms || [],
          test_cases: plan.test_cases || []
        };
        
        const response = await axios.post('/api/plan', minimalPlan);
        planId = response.data.id;
        setCreatedPlanId(planId);
        setMessage(`Plan initialized! You can now upload images. ID: ${planId}`);
        setMessageType('success');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        setMessage('Failed to initialize plan for image upload');
        setMessageType('error');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
    }

    try {
      const uploadPromises = [];

      for (let file of files) {
        const formData = new FormData();
        formData.append('image', file);
        uploadPromises.push(
          axios.post(`/api/plan/${planId}/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        );
      }

      const responses = await Promise.all(uploadPromises);
      const imagePaths = responses.map(response => response.data.image_path);
      
      setPlan(prev => ({
        ...prev,
        test_cases: prev.test_cases.map((testCase, i) => 
          i === selectedTestCaseIndex ? {
            ...testCase,
            steps: testCase.steps.map((step, stepI) => 
              stepI === stepIndex ? { 
                ...step, 
                question: {
                  ...step.question,
                  images: [...(step.question.images || []), ...imagePaths]
                }
              } : step
            )
          } : testCase
        )
      }));

      setMessage('Question images uploaded successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to upload question images');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleOptionImageUpload = async (stepIndex, optionText, files) => {
    let planId = createdPlanId || id;
    
    if (!planId) {
      try {
        // Create a minimal plan to get an ID
        const minimalPlan = {
          title: plan.title || 'Untitled Plan',
          description: plan.description || '',
          platforms: plan.platforms || [],
          test_cases: plan.test_cases || []
        };
        
        const response = await axios.post('/api/plan', minimalPlan);
        planId = response.data.id;
        setCreatedPlanId(planId);
        setMessage(`Plan initialized! You can now upload images. ID: ${planId}`);
        setMessageType('success');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        setMessage('Failed to initialize plan for image upload');
        setMessageType('error');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
    }

    try {
      const uploadPromises = [];

      for (let file of files) {
        const formData = new FormData();
        formData.append('image', file);
        uploadPromises.push(
          axios.post(`/api/plan/${planId}/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        );
      }

      const responses = await Promise.all(uploadPromises);
      const imagePaths = responses.map(response => response.data.image_path);
      
      setPlan(prev => ({
        ...prev,
        test_cases: prev.test_cases.map((testCase, i) => 
          i === selectedTestCaseIndex ? {
            ...testCase,
            steps: testCase.steps.map((step, stepI) => 
              stepI === stepIndex ? { 
                ...step, 
                question: {
                  ...step.question,
                  option_images: {
                    ...step.question.option_images,
                    [optionText]: [...(step.question.option_images?.[optionText] || []), ...imagePaths]
                  }
                }
              } : step
            )
          } : testCase
        )
      }));

      setMessage('Option images uploaded successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to upload option images');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const removeStep = (index) => {
    setPlan(prev => ({
      ...prev,
      test_cases: prev.test_cases.map((testCase, i) => 
        i === selectedTestCaseIndex ? {
          ...testCase,
          steps: testCase.steps.filter((_, stepI) => stepI !== index)
        } : testCase
      )
    }));
  };

  const toggleStepCollapse = (stepIndex) => {
    const stepKey = `${selectedTestCaseIndex}-${stepIndex}`;
    setCollapsedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepKey)) {
        newSet.delete(stepKey);
      } else {
        newSet.add(stepKey);
      }
      return newSet;
    });
  };

  const expandAllSteps = () => {
    setCollapsedSteps(new Set());
  };

  const collapseAllSteps = () => {
    const allStepIndices = new Set();
    if (plan.test_cases[selectedTestCaseIndex]) {
      plan.test_cases[selectedTestCaseIndex].steps.forEach((step, stepIndex) => {
        allStepIndices.add(`${selectedTestCaseIndex}-${stepIndex}`);
      });
    }
    setCollapsedSteps(allStepIndices);
  };

  const toggleTestCaseCollapse = (testCaseIndex) => {
    setCollapsedTestCases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(testCaseIndex)) {
        newSet.delete(testCaseIndex);
      } else {
        newSet.add(testCaseIndex);
      }
      return newSet;
    });
  };

  const expandAllTestCases = () => {
    setCollapsedTestCases(new Set());
  };

  const collapseAllTestCases = () => {
    const allTestCaseIndices = new Set();
    plan.test_cases.forEach((testCase, index) => {
      allTestCaseIndices.add(index);
    });
    setCollapsedTestCases(allTestCaseIndices);
  };

  const handleTestCaseImageUpload = async (testCaseIndex, files) => {
    let planId = createdPlanId || id;
    
    if (!planId) {
      try {
        // Create a minimal plan to get an ID
        const minimalPlan = {
          title: plan.title || 'Untitled Plan',
          description: plan.description || '',
          platforms: plan.platforms || [],
          test_cases: plan.test_cases || []
        };
        
        const response = await axios.post('/api/plan', minimalPlan);
        planId = response.data.id;
        setCreatedPlanId(planId);
        setMessage(`Plan initialized! You can now upload images. ID: ${planId}`);
        setMessageType('success');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        setMessage('Failed to initialize plan for image upload');
        setMessageType('error');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
    }

    try {
      const uploadPromises = [];

      for (let file of files) {
        const formData = new FormData();
        formData.append('image', file);
        uploadPromises.push(
          axios.post(`/api/plan/${planId}/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        );
      }

      const responses = await Promise.all(uploadPromises);
      const imagePaths = responses.map(response => response.data.image_path);
      
      setPlan(prev => ({
        ...prev,
        test_cases: prev.test_cases.map((testCase, i) => 
          i === testCaseIndex ? {
            ...testCase,
            ui_images: [...(testCase.ui_images || []), ...imagePaths]
          } : testCase
        )
      }));

      setMessage('Test case images uploaded successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to upload test case images');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleImageUpload = async (stepIndex, files) => {
    let planId = createdPlanId || id;
    
    if (!planId) {
      try {
        // Create a minimal plan to get an ID
        const minimalPlan = {
          title: plan.title || 'Untitled Plan',
          description: plan.description || '',
          platforms: plan.platforms || [],
          test_cases: plan.test_cases || []
        };
        
        const response = await axios.post('/api/plan', minimalPlan);
        planId = response.data.id;
        setCreatedPlanId(planId);
        setMessage(`Plan initialized! You can now upload images. ID: ${planId}`);
        setMessageType('success');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        setMessage('Failed to initialize plan for image upload');
        setMessageType('error');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
    }

    try {
      const uploadPromises = [];

      for (let file of files) {
        const formData = new FormData();
        formData.append('image', file);
        uploadPromises.push(
          axios.post(`/api/plan/${planId}/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        );
      }

      const responses = await Promise.all(uploadPromises);
      const imagePaths = responses.map(response => response.data.image_path);
      
      setPlan(prev => ({
        ...prev,
        test_cases: prev.test_cases.map((testCase, i) => 
          i === selectedTestCaseIndex ? {
            ...testCase,
            steps: testCase.steps.map((step, stepI) => 
              stepI === stepIndex ? { 
                ...step, 
                ui_images: [...(step.ui_images || []), ...imagePaths] 
              } : step
            )
          } : testCase
        )
      }));

      setMessage('Images uploaded successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to upload images');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    }
  };



  const savePlan = async () => {
    try {
      if (isEditMode && createdPlanId) {
        // Update existing plan
        await axios.post(`/api/plan/${createdPlanId}/save`, plan);
        setMessage('Plan updated successfully!');
        setMessageType('success');
        setTimeout(() => setMessage(''), 3000);
      } else if (createdPlanId) {
        // Update the plan that was already created during initialization
        await axios.post(`/api/plan/${createdPlanId}/save`, plan);
        setMessage('Plan saved successfully!');
        setMessageType('success');
        setTimeout(() => setMessage(''), 3000);
      } else {
        // Create new plan (fallback if somehow no ID exists)
        const response = await axios.post('/api/plan', plan);
        setCreatedPlanId(response.data.id);
        setMessage(`Plan created successfully! ID: ${response.data.id}`);
        setMessageType('success');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      setMessage(isEditMode ? 'Failed to update plan' : 'Failed to save plan');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading plan for editing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h1>{formatEmojiText('📝', isEditMode ? 'Edit Test Plan' : 'Test Plan Builder')}</h1>
        <p>{isEditMode ? 'Edit your existing test plan' : 'Create YAML-driven test plans with visual editor'}</p>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <Link to="/" className="btn btn-secondary" style={{ marginRight: '1rem' }}>← Back to Home</Link>
        {isEditMode && (
          <>
            <Link to={`/plan/${createdPlanId}`} className="btn btn-primary" style={{ marginRight: '1rem' }}>
              🧪 Run Tests
            </Link>
            <Link to={`/dashboard/${createdPlanId}`} className="btn btn-secondary">
              📊 View Dashboard
            </Link>
          </>
        )}
      </div>

      {message && (
        <div className={`alert alert-${messageType}`}>
          {message}
        </div>
      )}

      {createdPlanId && !isEditMode && (
        <div className="card">
          <h2>🎉 Plan Created Successfully!</h2>
          <p>Your test plan has been created with ID: <strong>{createdPlanId}</strong></p>
          <div style={{ marginTop: '1rem' }}>
            <Link to={`/plan/${createdPlanId}`} className="btn btn-primary" style={{ marginRight: '1rem' }}>
              🧪 Run Test Plan
            </Link>
            <button className="btn btn-secondary" onClick={() => {
              setCreatedPlanId('');
              setPlan({
                title: '',
                description: '',
                platforms: [],
                test_cases: []
              });
            }}>
              📝 Create Another Plan
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>📋 Plan Details</h2>
          {createdPlanId && !isEditMode && (
            <span style={{ 
              fontSize: '0.875rem', 
              color: '#10b981', 
              fontWeight: '500',
              padding: '0.25rem 0.75rem',
              backgroundColor: '#d1fae5',
              borderRadius: '16px',
              border: '1px solid #a7f3d0'
            }}>
              ✅ Plan ID: {createdPlanId}
            </span>
          )}
        </div>
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            className="form-control"
            value={plan.title}
            onChange={(e) => setPlan(prev => ({ ...prev, title: e.target.value }))}
            placeholder="e.g., Login Flow Test"
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea
            className="form-control"
            value={plan.description}
            onChange={(e) => setPlan(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Brief description of what this test plan covers"
            rows="3"
          />
        </div>
        <div className="form-group">
          <PlatformSelector 
            platforms={plan.platforms}
            onChange={(platforms) => setPlan(prev => ({ ...prev, platforms }))}
          />
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>🧪 Test Cases</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={expandAllTestCases} style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
              📖 Expand All
            </button>
            <button className="btn btn-secondary" onClick={collapseAllTestCases} style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
              📚 Collapse All
            </button>
          </div>
        </div>
        {plan.test_cases.map((testCase, index) => (
          <TestCase
            key={index}
            testCase={testCase}
            testCaseIndex={index}
            onUpdateTestCase={updateTestCase}
            onRemoveTestCase={removeTestCase}
            isCollapsed={collapsedTestCases.has(index)}
            onToggleCollapse={() => toggleTestCaseCollapse(index)}
            onImageUpload={handleTestCaseImageUpload}
            planId={createdPlanId || id}
            onImageClick={(image) => {
              setSelectedImage(image);
              setShowImageModal(true);
            }}
          />
        ))}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
          <button className="btn btn-primary" onClick={addTestCase}>
            ➕ Add Test Case
          </button>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={expandAllTestCases} style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
              📖 Expand All
            </button>
            <button className="btn btn-secondary" onClick={collapseAllTestCases} style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
              📚 Collapse All
            </button>
          </div>
        </div>
      </div>

      {plan.test_cases.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h2>🔧 Test Steps</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-secondary" onClick={expandAllSteps} style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
                  📖 Expand All
                </button>
                <button className="btn btn-secondary" onClick={collapseAllSteps} style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
                  📚 Collapse All
                </button>
              </div>
              <select
                className="form-control"
                style={{ width: 'auto' }}
                value={selectedTestCaseIndex}
                onChange={(e) => setSelectedTestCaseIndex(parseInt(e.target.value))}
              >
                {plan.test_cases.map((testCase, index) => (
                  <option key={index} value={index}>
                    {testCase.title || `Test Case ${index + 1}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {plan.test_cases[selectedTestCaseIndex]?.steps.map((step, index) => {
            const stepKey = `${selectedTestCaseIndex}-${index}`;
            const isCollapsed = collapsedSteps.has(stepKey);
            
            return (
            <div key={index} className="step-item">
              <div 
                className="step-header" 
                onClick={() => toggleStepCollapse(index)}
                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <strong>{step.title || `Step ${index + 1}`}</strong>
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    {step.step_type === 'question' ? '❓ Question' : step.step_type === 'instruction' ? '📖 Instruction' : '🧪 Test'} • {step.id || 'No ID'}
                  </span>
                </div>
                <div style={{ fontSize: '1.2rem' }}>
                  {isCollapsed ? '▶' : '▼'}
                </div>
              </div>
              
              {!isCollapsed && (
                <div className="step-content">
              <div className="form-group">
                <label>Step Type</label>
                <select
                  className="form-control"
                  value={step.step_type || 'test'}
                  onChange={(e) => {
                    const newType = e.target.value;
                    updateStep(index, 'step_type', newType);
                    if (newType === 'question' && !step.question) {
                      updateStep(index, 'question', {
                        type: 'single',
                        options: ['', ''],
                        images: [],
                        option_images: {},
                        required: false
                      });
                    } else if (newType === 'test' || newType === 'instruction') {
                      updateStep(index, 'question', null);
                    }
                  }}
                >
                  <option value="test">🧪 Test Step</option>
                  <option value="question">❓ Question Step</option>
                  <option value="instruction">📖 Instruction Step</option>
                </select>
              </div>
              <div className="form-group">
                <label>Step ID</label>
                <input
                  type="text"
                  className="form-control"
                  value={step.id}
                  onChange={(e) => updateStep(index, 'id', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={step.title}
                  onChange={(e) => updateStep(index, 'title', e.target.value)}
                  placeholder={step.step_type === 'question' ? 'e.g., Which browser are you using?' : step.step_type === 'instruction' ? 'e.g., Setup instructions' : 'e.g., Enter login credentials'}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-control"
                  value={step.description}
                  onChange={(e) => updateStep(index, 'description', e.target.value)}
                  placeholder={step.step_type === 'question' ? 'Additional context for the question' : step.step_type === 'instruction' ? 'Detailed instructions for setup or information' : 'Detailed description of what the user should do in this step'}
                  rows="2"
                />
              </div>
              
              {step.step_type === 'question' && step.question && (
                <QuestionStep
                  step={step}
                  stepIndex={index}
                  planId={createdPlanId || id}
                  onQuestionImageUpload={handleQuestionImageUpload}
                  onOptionImageUpload={handleOptionImageUpload}
                  onUpdateStep={updateStep}
                  onUpdateQuestionOption={updateQuestionOption}
                  onAddQuestionOption={addQuestionOption}
                  onRemoveQuestionOption={removeQuestionOption}
                  onImageClick={(image) => {
                    setSelectedImage(image);
                    setShowImageModal(true);
                  }}
                />
              )}
              
              <TestStep
                step={step}
                stepIndex={index}
                planId={createdPlanId || id}
                onImageUpload={handleImageUpload}
                onUpdateStep={updateStep}
                onImageClick={(image) => {
                  setSelectedImage(image);
                  setShowImageModal(true);
                }}
              />
              <StepConditionalLogic
                step={step}
                stepIndex={index}
                plan={plan}
                selectedTestCaseIndex={selectedTestCaseIndex}
                stepFilters={stepFilters}
                onUpdateStep={updateStep}
                onUpdateStepFilters={updateStepFilters}
              />
                <button 
                  className="btn btn-danger" 
                  onClick={() => removeStep(index)}
                  style={{ marginTop: '1rem' }}
                >
                  🗑️ Remove Step
                </button>
                         </div>
                       )}
                     </div>
                   );
                 })}
         <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
           <button className="btn btn-primary" onClick={() => addStep('test')}>
             ➕ Add Test Step
           </button>
           <button className="btn btn-secondary" onClick={() => addStep('question')}>
             ➕ Add Question Step
           </button>
           <button className="btn btn-secondary" onClick={() => addStep('instruction')}>
             ➕ Add Instruction Step
           </button>
         </div>
         
         <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
           <button className="btn btn-secondary" onClick={expandAllSteps} style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
             📖 Expand All
           </button>
           <button className="btn btn-secondary" onClick={collapseAllSteps} style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
             📚 Collapse All
           </button>
         </div>
       </div>
      )}

      <div className="yaml-editor">
        <div className="card">
          <h2>📝 Visual Editor</h2>
          <p>Use the form above to build your test plan visually.</p>
        </div>
        <div className="card">
          <h2>📄 YAML Editor</h2>
          <YamlEditor
            value={yamlText}
            onChange={setYamlText}
            placeholder="YAML will appear here..."
            height="500px"
            onFormat={(formatted) => {
              setYamlText(formatted);
              updatePlanFromYaml();
            }}
            onValidate={(result) => {
              if (result.valid) {
                setMessage('YAML is valid and ready to update plan!');
                setMessageType('success');
              } else {
                setMessage(`YAML validation failed: ${result.error}`);
                setMessageType('error');
              }
              setTimeout(() => setMessage(''), 3000);
            }}
          />
          <button 
            className="btn btn-secondary" 
            onClick={updatePlanFromYaml}
            style={{ marginTop: '1rem' }}
          >
            🔄 Update Plan from YAML
          </button>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={savePlan}>
            💾 {isEditMode ? 'Update Test Plan' : (createdPlanId ? 'Save Test Plan' : 'Create Test Plan')}
          </button>
          
          {isEditMode && createdPlanId && (
            <Link to={`/dashboard/${createdPlanId}`} className="btn btn-secondary">
              📊 View Dashboard
            </Link>
          )}
          
          {message && (
            <div style={{ 
              padding: '0.5rem 1rem', 
              borderRadius: '6px', 
              fontSize: '0.875rem',
              fontWeight: '500',
              backgroundColor: messageType === 'success' ? '#d1fae5' : '#fee2e2',
              color: messageType === 'success' ? '#065f46' : '#991b1b',
              border: `1px solid ${messageType === 'success' ? '#a7f3d0' : '#fecaca'}`
            }}>
              {messageType === 'success' ? '✅' : '❌'} {message}
            </div>
          )}
        </div>
      </div>



      <ImageModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImage(null);
        }}
        imageSrc={getImageUrl(selectedImage, createdPlanId || id)}
        imageAlt="Plan reference"
      />

      <Footer />
    </div>
  );
}

export default PlanBuilder; 